/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {EmpFile} from '../../../../shared/employees/employees.model';
import {
  ControlAndZoneDescriptor,
  GroupZoneDescriptor,
  Value,
  ZoneDescriptor,
  ZoneValues
} from '../../form-generator.interfaces';
import {DefaultValue, FormCardFieldComponent} from '../form-card-field/form-card-field.component';
import cloneDeep from "lodash-es/cloneDeep";

/**
 * Form card row component for the form generator.
 * Holds a line of fields in a card component
 */
@Component({
  selector: '[gp-form-card-row]',
  template: `
    <div class="row">
      <div #fieldsContainer></div>
      <!-- Fields injected here -->
    </div>
  `,
  styleUrls: ['./form-card-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormCardRowComponent implements AfterViewInit, OnDestroy {

  public zones: Array<GroupZoneDescriptor>;
  public cardDescriptor: GroupZoneDescriptor;
  public fields: Array<ComponentRef<FormCardFieldComponent>> = [];
  public fieldDescriptors: Array<ZoneDescriptor>;

  public descriptor: GroupZoneDescriptor;
  public group: UntypedFormGroup;
  public data: Array<ZoneValues>;
  public loaded: EventEmitter<ControlAndZoneDescriptor[]> = new EventEmitter();
  public readOnly: boolean = false;
  public dataIndex: number = 0;

  public locale: string;
  public currency: string;
  public editMode: boolean;
  public isOnBoarding = false;
  public employeeId: number = null;

  @ViewChild('fieldsContainer', {read: ViewContainerRef, static: true})
  private fieldsContainer: ViewContainerRef;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private componentFactoryResolver: ComponentFactoryResolver,
    private changeDetectorRef: ChangeDetectorRef
  ) {
  }

  public ngAfterViewInit(): any {
    this.loadFields().then(fields => {
      this.loaded.emit(fields);
      this.loaded.complete();
    });
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy() {
    this.fields.forEach(ref => {
      ref.destroy();
      ref = null;
    });
    this.fields = [];
  }

  private loadFields(): Promise<Array<FormCardFieldComponent>> {
    // Remove existing fields if any
    this.ngOnDestroy();
    // Load field components
    const promisesOfLoadedObservables: Array<Promise<FormCardFieldComponent>> =
      this.fieldDescriptors
        .map((fieldConfiguration: ZoneDescriptor) => {
          const fieldRef: ComponentRef<FormCardFieldComponent> = this.fieldsContainer.createComponent(
            this.componentFactoryResolver.resolveComponentFactory(FormCardFieldComponent));

          this.changeDetectorRef.markForCheck();

          const field: FormCardFieldComponent = fieldRef.instance,
            data = this.data[this.dataIndex || 0],
            value = (data && data.values) ?
              <Value>data.values[fieldConfiguration.zoneID] :
              new DefaultValue();

          this.fields.push(fieldRef);
          field.groupZoneDescriptor = this.cardDescriptor;
          // `_readOnly` = original value; `readOnly` = current (possibly mutated) value.
          if (fieldConfiguration._readOnly === undefined) {
            fieldConfiguration._readOnly = fieldConfiguration.readOnly;
          } else {
            fieldConfiguration.readOnly = fieldConfiguration._readOnly;
          }
          field.descriptor = fieldConfiguration;
          field.originalDescriptor = cloneDeep(fieldConfiguration);
          field.value = value ? value : new DefaultValue();
          field.cardColumns = this.cardDescriptor.nbCol;
          field.readOnly = this.readOnly || fieldConfiguration.readOnly;
          field.currency = this.currency;
          field.locale = this.locale;
          field.editMode = this.editMode;
          field.groupData = data;
          field.cardData = this.data;
          field.isOnBoarding = this.isOnBoarding;
          field.employeeId = this.employeeId;
          field.generateWithAiUsingFile = (empFile: EmpFile, event?: MouseEvent) =>
            this.generateWithAiUsingFile(empFile, event);

          return field.loaded.toPromise();
        });

    return Promise.all(promisesOfLoadedObservables);
  }

  public generateWithAiUsingFile(
    empFile: EmpFile,
    event?: MouseEvent
  ): void {
    /* fake here, will be overridden by form-card */
  }
}
